import { strIsNullOrEmpty, strTrim, tryParseJSONObject } from '@fox/atoms/utils/string';
import { PaymentMethod } from '@fox/finance/src/contracts';
import { PAYMENT_BLOCK_ANSWER_TYPE } from '../../../../form-block-types';
import { OverrideOption, PaymentBlockMetadataType } from '../../payment-block/contracts';
import { getPaymentAmounts } from '../../payment-block/helpers';
import { FormPageValidatorFunctionType } from './form-page-validators';

export function isAllowZeroPayment(meta: PaymentBlockMetadataType): boolean {
	return meta.allowZeroPayment === true || meta.alwaysCaptureMethod !== true;
}
/**
 *  Validates payment block by checking conditions and running payment block mutations (complete intents).
 * @param tempDataRef
 * @param answers
 * @param paymentBlock
 * @param allBlocks
 * @returns - Boolean, true if internal conditions leads to allowing form submission
 */

export const validatePaymentBlock: FormPageValidatorFunctionType = async ({ field, tempData, sections, answers, layout }) => {
	const selectedAnswerIndex = answers.findIndex(a => a.questionName === field.name);
	const selectedAnswer = answers[selectedAnswerIndex];
	const parsed = tryParseJSONObject<any>(selectedAnswer?.answer?.answerJson, {});
	const meta = tryParseJSONObject<PaymentBlockMetadataType>(field.metadataJson, {});
	const isStatementMethod = selectedAnswer?.data?.method === PaymentMethod.Statement;

	if (isStatementMethod && meta.invoice?.allowReferenceOverride === OverrideOption.Required && strIsNullOrEmpty(strTrim(parsed.invoiceReference))) {
		return { success: false, errorMessage: `${meta.invoice?.referenceLabelOverride || 'Reference number'} is required` };
	}

	const { grandTotal, lineItemCount } = getPaymentAmounts(sections, answers, undefined, meta, field.name, layout);
	// If there are no line items, we can skip the payment block validation
	if (lineItemCount === 0 && !meta.alwaysCaptureMethod) {
		if (selectedAnswerIndex >= 0 && isStatementMethod) {
			// Statement only
			// Remove irrelevant payment answer
			answers.splice(selectedAnswerIndex, 1);
		}

		return { success: true };
	}

	// Checking for <= 0 so that any discounting that would drive the total to negative is still considered
	// a "zero payment" in our eyes
	const zeroPayment = isAllowZeroPayment(meta) && grandTotal <= 0;

	if (!tempData?.contextSubmission) {
		//the context submission is triggered by a payment method component, so the payment would have already happened (so far only the paypal component does this)
		if (parsed.successfulPaymentToken || (parsed.methodId && !tempData?.dirty)) {
			// Check if payment information already exists and was successfully paid.
			if (tempData) tempData.dirty = false;
			return { success: true };
		} else if (isStatementMethod) {
			if (tempData) {
				tempData[field.name] = { method: PaymentMethod.Statement };
			}
		} else {
			//  Otherwise wipe card payment in temp data and continue
			if (tempData) {
				delete tempData[field.name];
			}
			if (tempData?.submitFunc) {
				try {
					await tempData.submitFunc?.submit();
				} catch (e) {
					return { success: false, errorMessage: 'Unable to process card' };
				}
				// Check if the form input is valid
				if ((!tempData.cardFormDataValid && !tempData.debitFormDataValid) || !tempData[field.name]) {
					return { success: false, errorMessage: 'Card input invalid' };
				}
				// Check if payment successful from response
				if (!tempData[field.name]?.success) {
					return { success: false, errorMessage: 'Unable to process card' };
				}
			} else {
				if (!zeroPayment) {
					if (meta.alwaysCaptureMethod) {
						return { success: false, errorMessage: 'Payment details required.' };
					}
					return { success: false, errorMessage: 'Something went wrong, please check payment details.' }; // This means we have payment info but no way to call payment mutations
					// If you are adding a new payment provider and have gotten here, make sure that you have a useImperativeHandle in your provider if you're not using standard <CardForm />
				}
			}
		}
	}
	const updatedAnswerPart = { ...tempData?.[field.name], invoiceReference: parsed.invoiceReference };
	const updatedAnswer = {
		questionName: field.name,
		answer: {
			answerJson: JSON.stringify(updatedAnswerPart)
		},
		data: selectedAnswer?.data,
		type: PAYMENT_BLOCK_ANSWER_TYPE
	};

	if (selectedAnswerIndex >= 0) {
		// If the answer has/not been saved
		answers[selectedAnswerIndex] = updatedAnswer;
	} else {
		answers.push(updatedAnswer);
	}
	if (tempData) {
		tempData.dirty = false;
	}
	return { success: true };
};
